import type { NodeReport, PodReport, Resources, UsageSample } from '../api/clusters';

export function formatCores(millicores: number): string {
  const cores = millicores / 1000;
  return `${cores < 10 ? cores.toFixed(2) : cores.toFixed(1)} cores`;
}

export function formatBytes(bytes: number): string {
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

export function formatPercent(value: number): string {
  return `${Math.round(value)}%`;
}

/** "1 pod needs attention", "3 pods need attention". */
export function attention(count: number): string {
  return `${count} ${count === 1 ? 'needs' : 'need'} attention`;
}

export function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/**
 * A failed pod has stopped for good, like a failed job step or a pod replaced
 * after a node restart. It is history, so the page hides it unless asked.
 */
export function hasFailed(pod: PodReport): boolean {
  return pod.phase === 'Failed';
}

/** A live pod needs attention when it is not fully running and ready. */
export function hasProblem(pod: PodReport): boolean {
  return (
    !hasFailed(pod) &&
    (pod.phase !== 'Running' || pod.readyContainers < pod.containers || pod.reason !== null)
  );
}

/**
 * Pods that count against a node's pod capacity. The Receptor leaves out
 * succeeded pods, so only failed ones are terminated.
 */
function isActive(pod: PodReport): boolean {
  return !hasFailed(pod);
}

export type GroupBy = 'node' | 'namespace';

/** The page's filters, kept in the URL so a drill-down can be shared. */
export interface ClustersSearch {
  cluster?: string;
  node?: string;
  namespace?: string;
  q?: string;
  group?: GroupBy;
  attention?: boolean;
  failed?: boolean;
}

export function validateClustersSearch(search: Record<string, unknown>): ClustersSearch {
  const text = (value: unknown) => (typeof value === 'string' && value ? value : undefined);
  const flag = (value: unknown) => (value === true || value === 'true' ? true : undefined);
  return {
    cluster: text(search.cluster),
    node: text(search.node),
    namespace: text(search.namespace),
    q: text(search.q),
    group: search.group === 'namespace' ? 'namespace' : undefined,
    attention: flag(search.attention),
    failed: flag(search.failed),
  };
}

/** True when a filter narrows the pods, so empty groups are hidden and the rest open. */
export function narrowsPods(search: ClustersSearch): boolean {
  return Boolean(search.namespace || search.q?.trim() || search.attention || search.failed);
}

/** The pods the filters keep. Failed pods show only when asked for, and then alone. */
export function filterPods(pods: PodReport[], search: ClustersSearch): PodReport[] {
  const needle = search.q?.trim().toLowerCase() ?? '';
  return pods.filter(
    pod =>
      (search.failed ? hasFailed(pod) : !hasFailed(pod)) &&
      (!search.attention || search.failed || hasProblem(pod)) &&
      (!search.node || pod.node === search.node) &&
      (!search.namespace || pod.namespace === search.namespace) &&
      (!needle ||
        [pod.namespace, pod.name, pod.node ?? '', pod.owner?.name ?? ''].some(value =>
          value.toLowerCase().includes(needle)
        ))
  );
}

/** A used figure against its total. `used` is null when no source reported it. */
export interface Usage {
  used: number | null;
  total: number;
}

export interface ClusterTotals {
  nodes: number;
  readyNodes: number;
  cpu: Usage;
  memory: Usage;
  disk: Usage;
  pods: Usage;
  /** Nodes whose kubelet did not answer, left out of CPU and memory. */
  nodesWithoutUsage: number;
  problems: number;
}

export function rootDisk(node: NodeReport) {
  return node.disks?.find(disk => disk.mountPoint === '/');
}

/**
 * The whole cluster's figures. CPU and memory add up only the nodes that
 * reported usage, against those nodes' capacity, so a silent node does not
 * look idle.
 */
export function clusterTotals(nodes: NodeReport[], pods: PodReport[]): ClusterTotals {
  const reporting = nodes.filter(node => node.usage);
  const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);
  const usage = (key: keyof Resources): Usage => ({
    used: reporting.length ? sum(reporting.map(node => node.usage![key])) : null,
    total: sum(reporting.map(node => node.capacity[key])),
  });
  const disks = nodes.flatMap(node => {
    const root = rootDisk(node);
    return root ? [root] : [];
  });
  return {
    nodes: nodes.length,
    readyNodes: nodes.filter(node => node.ready).length,
    cpu: usage('cpuMillicores'),
    memory: usage('memoryBytes'),
    disk: {
      used: disks.length ? sum(disks.map(disk => disk.usedBytes)) : null,
      total: sum(disks.map(disk => disk.capacityBytes)),
    },
    pods: {
      used: pods.filter(isActive).length,
      total: sum(nodes.map(node => node.allocatable.pods)),
    },
    nodesWithoutUsage: nodes.length - reporting.length,
    problems: pods.filter(hasProblem).length,
  };
}

export function activePodsOn(node: string, pods: PodReport[]): number {
  return pods.filter(pod => pod.node === node && isActive(pod)).length;
}

export interface PodGroup {
  key: string;
  pods: PodReport[];
  problems: number;
  restarts: number;
  /** Null when no pod in the group reported usage. */
  usage: Resources | null;
  requests: Resources;
}

/** Pods grouped by `key`, groups with problems first, then by name. */
export function groupPods(pods: PodReport[], key: (pod: PodReport) => string): PodGroup[] {
  const groups = new Map<string, PodReport[]>();
  for (const pod of pods) {
    const name = key(pod);
    const group = groups.get(name);
    if (group) group.push(pod);
    else groups.set(name, [pod]);
  }
  return [...groups]
    .map(([name, members]): PodGroup => {
      const measured = members.filter(pod => pod.usage);
      return {
        key: name,
        pods: members,
        problems: members.filter(hasProblem).length,
        restarts: members.reduce((total, pod) => total + pod.restarts, 0),
        usage: measured.length
          ? {
              cpuMillicores: measured.reduce((t, pod) => t + pod.usage!.cpuMillicores, 0),
              memoryBytes: measured.reduce((t, pod) => t + pod.usage!.memoryBytes, 0),
            }
          : null,
        requests: {
          cpuMillicores: members.reduce((t, pod) => t + pod.requests.cpuMillicores, 0),
          memoryBytes: members.reduce((t, pod) => t + pod.requests.memoryBytes, 0),
        },
      };
    })
    .sort((a, b) => Number(b.problems > 0) - Number(a.problems > 0) || a.key.localeCompare(b.key));
}

export interface TrendPoint {
  at: number;
  value: number | null;
}

/**
 * One figure over the history: the sum over `nodes`, or a gap where any of
 * them did not report, so a missing kubelet never reads as a drop.
 */
export function trend(history: UsageSample[], nodes: string[], key: keyof Resources): TrendPoint[] {
  return history.map(sample => {
    let value: number | null = 0;
    for (const node of nodes) {
      const usage = sample.nodes[node];
      if (!usage) {
        value = null;
        break;
      }
      value += usage[key];
    }
    return { at: Date.parse(sample.at), value: nodes.length ? value : null };
  });
}
